import { useState, useEffect, useCallback } from "react";
import { useTournamentAuth } from "../utils/authorization";
import { useTournamentId, useTournamentSlug } from "../context/TournamentRouteContext";
import { useTournamentFinalizationStatus } from "./useTournamentFinalizationStatus";
import { supabase } from "../supabaseClient";
import { addCurrency } from "../utils/currencyUtils";
import { getFinanceUrl, getSetupUrl } from "../utility/adminNavigation";
import { tournamentPath } from "../utility/tournamentUrls";
import {
  getPublicProgressSteps,
} from "../utility/tournamentProgress";
import {
  FaUsers,
  FaTrophy,
  FaFileAlt,
  FaDollarSign,
  FaCheckCircle,
  FaClipboardList,
} from "react-icons/fa";

export const TOURNAMENT_STEPS = {
  EVENTS_CREATED: {
    key: "hasEvents",
    label: "Events Created",
    inProgressLabel: "Creating Events",
    description: "Add the events this tournament will bowl",
    icon: FaTrophy,
    link: (tournamentId) => `${getSetupUrl(tournamentId)}#setup-events`,
    action: "Go to Setup",
    priority: 1,
    isComplete: (status) => status.hasEvents,
  },

  EVENT_FEES_SET: {
    key: "allEventsHaveFees",
    label: "Event Fees Set",
    inProgressLabel: "Setting Event Fees",
    description: "Set fees for every event",
    icon: FaDollarSign,
    link: (tournamentId) => `${getSetupUrl(tournamentId)}#setup-events`,
    action: "Go to Setup",
    priority: 2,
    isComplete: (status) => status.allEventsHaveFees,
  },

  SQUADS_CREATED: {
    key: "hasSquads",
    label: "Squads Created",
    inProgressLabel: "Creating Squads",
    description: "Set the house, dates, and times",
    icon: FaUsers,
    link: (tournamentId) => `${getSetupUrl(tournamentId)}#setup-squads`,
    action: "Go to Squads",
    priority: 3,
    isComplete: (status) => status.hasSquads,
  },

  SCORES_ENTERED: {
    key: "allTeamScoresComplete",
    label: "Scoring Complete",
    inProgressLabel: "Entering Scores",
    description: "Every squad has bowlers and every bowler has scores",
    icon: FaClipboardList,
    link: (tournamentId) => tournamentPath(tournamentId, "/scoring"),
    action: "Go to Scoring",
    priority: 4,
    isComplete: (status) => status.allTeamScoresComplete,
  },

  AVERAGES_VERIFIED: {
    key: "averagesVerified",
    label: "Averages Verified",
    inProgressLabel: "Verifying Averages",
    description: "Verify bowler averages and calculate handicaps",
    icon: FaCheckCircle,
    link: (tournamentId) => tournamentPath(tournamentId, "/averages"),
    action: "Go to Averages",
    priority: 5,
    isComplete: (status) => status.averagesVerified,
  },

  SCORES_VERIFIED: {
    key: "teamsVerified",
    label: "Scores Verified",
    inProgressLabel: "Verifying Scores",
    description: "Review and verify all team scores are correct",
    icon: FaCheckCircle,
    link: (tournamentId) => tournamentPath(tournamentId, "/scoring"),
    action: "Go to Scoring",
    priority: 6,
    isComplete: (status) => status.teamsVerified,
  },

  PAYOUTS_FINALIZED: {
    key: "payoutFinalized",
    label: "Payouts Finalized",
    inProgressLabel: "Finalizing Payouts",
    description: "Finalize prize money distribution",
    icon: FaDollarSign,
    link: (tournamentId) => getFinanceUrl(tournamentId, "payouts"),
    action: "Go to Payouts",
    priority: 7,
    isComplete: (status) => status.payoutFinalized,
  },

  RESULTS_FINALIZED: {
    key: "resultsFinalized",
    label: "Standings Finalized",
    inProgressLabel: "Finalizing Standings",
    description: "Publish final standings",
    icon: FaFileAlt,
    link: (tournamentId) => tournamentPath(tournamentId, "/standings"),
    action: "Go to Standings",
    priority: 8,
    isComplete: (status) => status.resultsFinalized,
  },
};

export const TOURNAMENT_STEPS_ARRAY = Object.values(TOURNAMENT_STEPS).sort(
  (a, b) => a.priority - b.priority
);

export const TOURNAMENT_STEPS_REFRESH_EVENT = "bta-tournament-steps-refresh";

export const requestTournamentStepsRefresh = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(TOURNAMENT_STEPS_REFRESH_EVENT));
};

export const useTournamentSteps = (tournamentIdParam, refreshTrigger = 0) => {
  const urlTournamentId = useTournamentId();
  const tournamentSlug = useTournamentSlug();
  const tournamentId = tournamentIdParam || urlTournamentId;
  const [eventRevision, setEventRevision] = useState(0);

  const { canManage } = useTournamentAuth(tournamentId);
  const { status, loading, error } = useTournamentFinalizationStatus(
    tournamentId,
    refreshTrigger + eventRevision
  );

  useEffect(() => {
    const onRefresh = () => setEventRevision((value) => value + 1);
    window.addEventListener(TOURNAMENT_STEPS_REFRESH_EVENT, onRefresh);
    return () => {
      window.removeEventListener(TOURNAMENT_STEPS_REFRESH_EVENT, onRefresh);
    };
  }, []);
  const [squadData, setSquadData] = useState(null);
  const [squadLoading, setSquadLoading] = useState(false);
  const [prizeFundData, setPrizeFundData] = useState(null);
  const [prizeFundLoading, setPrizeFundLoading] = useState(false);

  useEffect(() => {
    if (!tournamentId || !canManage || loading) return;

    const fetchSquadData = async () => {
      setSquadLoading(true);
      try {
        const { data: squads, error: squadsError } = await supabase
          .from("squads")
          .select(
            `
            id,
            squadDate,
            squadTime,
            teams (
              id,
              teamName,
              isDeleted,
              scores (
                id,
                score
              )
            )
          `
          )
          .eq("tournamentID", tournamentId)
          .eq("isDeleted", false)
          .eq("teams.isDeleted", false)
          .order("squadDate", { ascending: true })
          .order("squadTime", { ascending: true });

        if (squadsError) throw squadsError;

        const teamIds = squads.flatMap((squad) =>
          squad.teams ? squad.teams.map((team) => team.id) : []
        );

        let teamVerificationStates = [];
        if (teamIds.length > 0) {
          const { data: verificationData, error: verificationError } =
            await supabase
              .from("verificationStates")
              .select("entityId, isVerified")
              .eq("entityType", "scores")
              .in("entityId", teamIds);

          if (verificationError) throw verificationError;
          teamVerificationStates = verificationData || [];
        }

        const processedSquads = squads.map((squad, index) => {
          const getDayOfWeek = (dateString) => {
            const [year, month, day] = dateString.split("-").map(Number);
            const date = new Date(year, month - 1, day, 12, 0, 0);
            return date.toLocaleDateString("en-US", { weekday: "long" });
          };

          const formatTime = (timeString) => {
            const [hours, minutes] = timeString.split(":");
            const hour = parseInt(hours);
            const period = hour >= 12 ? "PM" : "AM";
            const displayHour = hour > 12 ? hour - 12 : hour;
            return `${displayHour}:${minutes} ${period}`;
          };

          const formatDate = (dateString) => {
            const [year, month, day] = dateString.split("-").map(Number);
            const date = new Date(year, month - 1, day, 12, 0, 0);
            return date.toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            });
          };

          const dayOfWeek = getDayOfWeek(squad.squadDate);
          const time = formatTime(squad.squadTime);
          const date = formatDate(squad.squadDate);
          const formattedDateTime = `${dayOfWeek}, ${date} at ${time}`;
          const squadDateTime = new Date(squad.squadDate);

          const liveTeams = squad.teams
            ? squad.teams.filter((team) => !team.isDeleted)
            : [];
          const teamsWithScores = liveTeams.filter(
            (team) => team.scores && team.scores.length > 0
          );

          const hasTeams = liveTeams.length > 0;
          const hasScores = teamsWithScores.length > 0;
          const allTeamsHaveScores =
            hasTeams && teamsWithScores.length === liveTeams.length;

          const verifiedTeams = liveTeams.filter((team) => {
            const verification = teamVerificationStates.find(
              (vs) => vs.entityId === team.id
            );
            return verification?.isVerified === true;
          });
          const allTeamsVerified =
            hasTeams && verifiedTeams.length === liveTeams.length;

          return {
            id: squad.id,
            name: formattedDateTime,
            dateTime: squadDateTime,
            hasTeams,
            hasScores,
            allTeamsHaveScores,
            allTeamsVerified,
            teamCount: liveTeams.length,
            teamsWithScoresCount: teamsWithScores.length,
            verifiedTeamsCount: verifiedTeams.length,
            index,
          };
        });

        setSquadData(processedSquads);
      } catch (error) {
        console.error("❌ ERROR: Error fetching squad data:", error);
      } finally {
        setSquadLoading(false);
      }
    };

    fetchSquadData();
  }, [tournamentId, canManage, loading]);

  const fetchPrizeFundData = useCallback(async () => {
    if (!tournamentId || !canManage || loading) return;

    setPrizeFundLoading(true);
    try {
      const { data: tournamentPrizeFunds, error: tournamentError } =
        await supabase
          .from("tournamentAddedPrizeFund")
          .select("amount")
          .eq("tournamentID", tournamentId);

      if (tournamentError) throw tournamentError;

      const { data: events, error: eventsError } = await supabase
        .from("tournamentEvents")
        .select("id")
        .eq("tournamentID", tournamentId)
        .eq("isDeleted", false);

      if (eventsError) throw eventsError;

      let totalAssigned = 0;
      if (events && events.length > 0) {
        const eventIds = events.map((event) => event.id);
        const { data: eventPrizeFunds, error: eventError } = await supabase
          .from("eventAddedPrizeFund")
          .select("amount")
          .in("eventsID", eventIds);

        if (eventError) throw eventError;
        totalAssigned = (eventPrizeFunds || []).reduce(
          (sum, fund) => addCurrency(sum, parseFloat(fund.amount) || 0),
          0
        );
      }

      const totalAvailable = (tournamentPrizeFunds || []).reduce(
        (sum, fund) => addCurrency(sum, parseFloat(fund.amount) || 0),
        0
      );
      const remaining = addCurrency(totalAvailable, -totalAssigned);

      setPrizeFundData({
        totalAvailable,
        totalAssigned,
        remaining,
      });
    } catch (error) {
      console.error("Error fetching prize fund data:", error);
      setPrizeFundData({ totalAvailable: 0, totalAssigned: 0, remaining: 0 });
    } finally {
      setPrizeFundLoading(false);
    }
  }, [tournamentId, canManage, loading]);

  useEffect(() => {
    if (!tournamentId || !canManage || loading) return;

    fetchPrizeFundData();

    const tournamentPrizeFundChannel = supabase
      .channel("tournament_prize_fund_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "tournamentAddedPrizeFund",
        },
        (payload) => {
          if (payload.new?.tournamentID === tournamentId) {
            fetchPrizeFundData();
          }
        }
      )
      .subscribe();

    const eventPrizeFundChannel = supabase
      .channel("event_prize_fund_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "eventAddedPrizeFund",
        },
        () => {
          fetchPrizeFundData();
        }
      )
      .subscribe();

    return () => {
      tournamentPrizeFundChannel.unsubscribe();
      eventPrizeFundChannel.unsubscribe();
    };
  }, [tournamentId, canManage, loading, fetchPrizeFundData]);

  const getAllSteps = () => {
    if (!status) return [];

    return TOURNAMENT_STEPS_ARRAY.map((step) => ({
      ...step,
      isComplete: step.isComplete(status),
      link: step.link(tournamentSlug || tournamentId),
    }));
  };

  const getNextStep = () => {
    if (
      loading ||
      squadLoading ||
      prizeFundLoading ||
      !canManage ||
      !status
    ) {
      return null;
    }

    for (const step of TOURNAMENT_STEPS_ARRAY) {
      if (step.isComplete(status)) continue;

      if (step.key === "allTeamScoresComplete") {
        const needsTeams =
          !squadData ||
          squadData.length === 0 ||
          squadData.some((squad) => !squad.hasTeams);

        if (needsTeams) {
          return {
            title: "Add Teams",
            inProgressLabel: "Adding Teams",
            description: "Add teams on Scoring.",
            icon: FaUsers,
            link: tournamentPath(tournamentSlug || tournamentId, "/scoring"),
            action: "Go to Scoring",
            priority: step.priority,
          };
        }

        for (const squad of squadData) {
          if (squad.hasTeams && !squad.allTeamsHaveScores) {
            return {
              title: `Enter Scores for ${squad.name}`,
              inProgressLabel: `Entering Scores for ${squad.name}`,
              description: `Enter team scores for this squad (${squad.teamsWithScoresCount}/${squad.teamCount} teams have scores)`,
              icon: FaClipboardList,
              link: tournamentPath(tournamentSlug || tournamentId, "/scoring"),
              action: "Go to Scoring",
              priority: step.priority,
            };
          }
        }
      }

      if (step.key === "teamsVerified" && squadData) {
        for (const squad of squadData) {
          if (squad.hasTeams && !squad.allTeamsVerified) {
            return {
              title: `Verify Scores for ${squad.name}`,
              inProgressLabel: `Verifying Scores for ${squad.name}`,
              description: `Review and verify all team scores in this squad (${squad.verifiedTeamsCount}/${squad.teamCount} teams verified)`,
              icon: FaCheckCircle,
              link: tournamentPath(tournamentSlug || tournamentId, "/scoring"),
              action: "Go to Scoring",
              priority: step.priority,
            };
          }
        }
      }

      if (
        step.key === "payoutFinalized" &&
        prizeFundData &&
        prizeFundData.remaining > 0
      ) {
        return {
          title: "Assign Additional Prize Money",
          inProgressLabel: "Assigning Additional Prize Money",
          description: "Distribute any additional prize funds to events",
          icon: FaDollarSign,
          link: getFinanceUrl(tournamentSlug || tournamentId, "added-money"),
          action: "Go to Payouts",
          priority: step.priority - 0.5,
        };
      }

      return {
        title: step.label,
        inProgressLabel: step.inProgressLabel,
        description: step.description,
        icon: step.icon,
        link: step.link(tournamentSlug || tournamentId),
        action: step.action,
        priority: step.priority,
      };
    }

    return null;
  };

  const getPublicSteps = () =>
    getPublicProgressSteps(status).map((step) => ({
      key: step.key,
      label: step.label,
      inProgressLabel: step.inProgressLabel,
      isComplete: step.isComplete,
    }));

  return {
    status,
    loading: loading || squadLoading || prizeFundLoading,
    error,
    squadData,
    prizeFundData,
    getAllSteps,
    getNextStep,
    getPublicSteps,
    canManage,
  };
};
